import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Link2, Users, Search, ChevronDown, ChevronUp, X,
  Award, ArrowLeft, ExternalLink, ScrollText,
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useDebounce } from "@/hooks/useDebounce";
import {
  errorMessage, track, useHadith, useHadiths, useIsnadFilters, useNarrator, useNarrators, usePageView,
} from "@/lib/api";
import type {
  ChainLinkResponse, ChainResponse, HadithDetail, HadithSummary, NarratorGeneration, NarratorSummary,
} from "@/lib/api";
import { Section, AnimatedCard } from "@/components/ui/shared";
import { SkeletonCardGrid } from "@/components/ui/Skeleton";
import { EmptyState, ErrorState, Pagination } from "@/components/ui/States";

const PAGE_SIZE = 20;

/* ============================================
   Generation Color Mapping
============================================ */

const GEN_COLORS: Record<NarratorGeneration, { bg: string; text: string; label: string }> = {
  prophet: { bg: "bg-gradient-to-br from-emerald-500 to-teal-600", text: "text-white", label: "The Prophet ﷺ" },
  sahabi: { bg: "bg-gradient-to-br from-[var(--color-gold)] to-amber-600", text: "text-white", label: "Companion (Sahabi)" },
  tabii: { bg: "bg-gradient-to-br from-blue-500 to-indigo-600", text: "text-white", label: "Successor (Tabi'i)" },
  tabi_tabii: { bg: "bg-gradient-to-br from-purple-500 to-pink-600", text: "text-white", label: "Successor of a Successor" },
  atba_tabiin: { bg: "bg-gradient-to-br from-rose-500 to-red-600", text: "text-white", label: "Fourth generation" },
  later: { bg: "bg-gradient-to-br from-gray-500 to-slate-600", text: "text-white", label: "Later scholar / collector" },
};

const UNKNOWN_GEN = { bg: "bg-gray-400", text: "text-white", label: "Unknown generation" };

function genColor(gen: string | null | undefined) {
  return (gen && GEN_COLORS[gen as NarratorGeneration]) || UNKNOWN_GEN;
}

const label = (value: string) => value.replace(/_/g, " ");

/* ============================================
   Narrator Biography Modal
============================================ */

function NarratorModal({ narratorId, onClose }: { narratorId: string; onClose: () => void }) {
  const { data: narrator, isLoading, isError, error } = useNarrator(narratorId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const colors = genColor(narrator?.generation);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={narrator ? `Biography of ${narrator.nameEnglish}` : "Narrator biography"}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
      >
        {isLoading ? (
          <p className="text-center text-[var(--color-ink-muted)] p-8">Loading narrator…</p>
        ) : isError || !narrator ? (
          <div className="p-8 text-center">
            <p className="text-[var(--color-ink-muted)] mb-4">{errorMessage(error)}</p>
            <button onClick={onClose} className="btn-outline">Close</button>
          </div>
        ) : (
          <>
            <div className={`${colors.bg} ${colors.text} p-6 relative`}>
              <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-lg transition-colors" aria-label="Close" autoFocus>
                <X className="w-5 h-5" />
              </button>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Users className="w-8 h-8" aria-hidden />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold mb-1">{narrator.nameEnglish}</h2>
                  {narrator.nameArabic && <p lang="ar" className="text-xl font-arabic mb-2">{narrator.nameArabic}</p>}
                  {narrator.kuniya && <p className="text-sm opacity-90">{narrator.kuniya}</p>}
                  <p className="text-sm opacity-90 mt-1">{colors.label}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {(narrator.birthYearHijri || narrator.deathYearHijri || narrator.birthPlace || narrator.deathPlace) && (
                <dl className="grid grid-cols-2 gap-4 text-sm">
                  {(narrator.birthYearHijri || narrator.birthPlace) && (
                    <div>
                      <dt className="text-[var(--color-ink-muted)] text-xs">Born</dt>
                      <dd className="text-[var(--color-midnight)]">
                        {[narrator.birthYearHijri && `${narrator.birthYearHijri} AH`, narrator.birthPlace].filter(Boolean).join(", ")}
                      </dd>
                    </div>
                  )}
                  {(narrator.deathYearHijri || narrator.deathPlace) && (
                    <div>
                      <dt className="text-[var(--color-ink-muted)] text-xs">Died</dt>
                      <dd className="text-[var(--color-midnight)]">
                        {[narrator.deathYearHijri && `${narrator.deathYearHijri} AH`, narrator.deathPlace].filter(Boolean).join(", ")}
                      </dd>
                    </div>
                  )}
                </dl>
              )}

              {narrator.bio && <p className="text-sm text-[var(--color-ink)] leading-relaxed">{narrator.bio}</p>}

              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-1 flex items-center gap-2">
                  <Award className="w-4 h-4" aria-hidden /> Reliability: <span className="capitalize">{label(narrator.reliability)}</span>
                </h3>
                {narrator.reliabilityNote && <p className="text-sm text-blue-800">{narrator.reliabilityNote}</p>}
                {narrator.scholarGradings && Object.keys(narrator.scholarGradings).length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {Object.entries(narrator.scholarGradings).map(([scholar, grading]) => (
                      <li key={scholar} className="text-xs text-blue-800">
                        <strong>{scholar}:</strong> {String(grading)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {[
                { title: "Teachers", people: narrator.teachers },
                { title: "Students", people: narrator.students },
              ].map(({ title, people }) =>
                people.length > 0 ? (
                  <div key={title}>
                    <h3 className="text-sm font-semibold text-[var(--color-midnight)] mb-2">{title} ({people.length})</h3>
                    <ul className="space-y-2">
                      {people.map((person) => {
                        const c = genColor(person.generation);
                        return (
                          <li key={person.id} className="flex items-center gap-2 p-2 rounded-lg bg-[var(--color-cream)]">
                            <span className={`w-8 h-8 rounded-full ${c.bg} ${c.text} flex items-center justify-center text-xs font-bold`} aria-hidden>
                              {person.nameEnglish[0]}
                            </span>
                            <span className="text-sm text-[var(--color-midnight)]">{person.nameEnglish}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null,
              )}

              <div className="border-t border-[var(--color-cream-dark)] pt-4">
                <p className="text-xs text-[var(--color-ink-muted)]">
                  Appears in <span className="font-semibold text-[var(--color-midnight)]">{narrator.hadithCount}</span>{" "}
                  {narrator.hadithCount === 1 ? "hadith" : "hadiths"} in this library
                  {narrator.isCompanion && (
                    <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                      Companion of the Prophet ﷺ
                    </span>
                  )}
                </p>
              </div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

/* ============================================
   Chain Visualization with Clickable Narrators
============================================ */

function ChainVisualization({
  chain,
  showAll,
  onNarratorClick,
}: {
  chain: ChainResponse;
  showAll: boolean;
  onNarratorClick: (narratorId: string) => void;
}) {
  const links = [...chain.links].sort((a, b) => a.position - b.position);
  const displayLinks = showAll ? links : links.slice(0, 3);

  if (links.length === 0) {
    return <div className="text-center py-8 text-sm text-[var(--color-ink-muted)]">Chain data not yet available for this hadith.</div>;
  }

  return (
    <div>
      <div className="text-center mb-4 flex flex-wrap justify-center gap-2">
        {chain.isComplete ? (
          <span className="text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium inline-flex items-center gap-1">
            <Award className="w-3 h-3" aria-hidden /> Complete chain to the Prophet ﷺ
          </span>
        ) : (
          <span className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Partial chain</span>
        )}
        {chain.chainGrading && (
          <span className="text-xs px-3 py-1 rounded-full bg-[var(--color-cream)] text-[var(--color-gold-dim)] font-medium">
            Grading: {chain.chainGrading}
          </span>
        )}
      </div>

      <ol className="relative max-w-sm mx-auto">
        <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[var(--color-gold)] to-[var(--color-gold)]/20 -translate-x-1/2" aria-hidden />

        {displayLinks.map((link: ChainLinkResponse, i: number) => {
          const { narrator } = link;
          const colors = genColor(narrator.generation);
          return (
            <motion.li
              key={`${link.position}-${narrator.id}`}
              initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="relative flex flex-col items-center mb-4 last:mb-0"
            >
              <button
                onClick={() => onNarratorClick(narrator.id)}
                className="flex flex-col items-center rounded-lg px-2 py-1 hover:bg-[var(--color-cream)] transition-colors group"
                aria-label={`${narrator.nameEnglish}, ${colors.label}. View biography`}
              >
                <span className={`w-12 h-12 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center shadow-lg z-10 relative group-hover:scale-110 transition-transform`}>
                  <span className="text-xs font-bold">{link.position}</span>
                </span>
                <span className="mt-2 text-sm font-semibold text-[var(--color-midnight)]">{narrator.nameEnglish}</span>
                <span className="text-[10px] text-[var(--color-ink-muted)]">{colors.label}</span>
              </button>

              {i < displayLinks.length - 1 && link.transmissionMethod && (
                <span className="mt-1 text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-cream)] text-[var(--color-gold-dim)] font-medium">
                  {label(link.transmissionMethod)}
                </span>
              )}
            </motion.li>
          );
        })}
      </ol>

      {chain.notes && (
        <p className="mt-6 p-4 bg-[var(--color-cream)] rounded-lg border border-[var(--color-cream-dark)] text-sm text-[var(--color-ink)]">
          {chain.notes}
        </p>
      )}

      <div className="flex flex-wrap justify-center gap-4 mt-8 pt-4 border-t border-[var(--color-cream-dark)]" aria-label="Generation legend">
        {Object.entries(GEN_COLORS).map(([gen, colors]) => (
          <div key={gen} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-full ${colors.bg}`} aria-hidden />
            <span className="text-[10px] text-[var(--color-ink-muted)]">{colors.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================
   Hadith Detail View
============================================ */

function HadithDetailView({ hadith, onBack }: { hadith: HadithDetail; onBack: () => void }) {
  const [selectedChainIndex, setSelectedChainIndex] = useState(0);
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [selectedNarratorId, setSelectedNarratorId] = useState<string | null>(null);

  const { collection, hadithNumber, textEnglish, textArabic, bookName, bookNumber, grading, chapter, chains } = hadith;
  const selectedChain = chains[selectedChainIndex];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-[var(--color-cream)] rounded-lg transition-colors" aria-label="Back to hadith list">
          <ArrowLeft className="w-5 h-5 text-[var(--color-ink-muted)]" />
        </button>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[var(--color-gold)]">{collection}</span>
            <span className="text-xs text-[var(--color-ink-muted)]">#{hadithNumber}</span>
            {grading && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-cream)] text-[var(--color-gold-dim)] font-medium">{grading}</span>
            )}
          </div>
          {bookName && (
            <p className="text-xs text-[var(--color-ink-muted)] mt-1">
              {bookName} {bookNumber && `• Book ${bookNumber}`}
            </p>
          )}
        </div>
      </div>

      <div className="card-elevated p-6 space-y-4">
        {textArabic && (
          <p lang="ar" dir="rtl" className="text-lg font-arabic text-right leading-loose text-[var(--color-midnight)]">{textArabic}</p>
        )}
        <p className="text-base leading-relaxed text-[var(--color-midnight)]">{textEnglish}</p>
        {chapter && <p className="text-xs text-[var(--color-ink-muted)] pt-2 border-t border-[var(--color-cream-dark)]">Chapter: {chapter}</p>}
      </div>

      {chains.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h3 className="text-lg font-semibold text-[var(--color-midnight)] flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[var(--color-gold)]" aria-hidden /> Chains of narration ({chains.length})
            </h3>
            {chains.length > 1 && (
              <div className="flex gap-2" role="tablist">
                {chains.map((chain, idx) => (
                  <button
                    key={chain.id}
                    role="tab"
                    aria-selected={idx === selectedChainIndex}
                    onClick={() => {
                      setSelectedChainIndex(idx);
                      setShowAllLinks(false);
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
                      idx === selectedChainIndex
                        ? "bg-[var(--color-gold)] text-white"
                        : "bg-[var(--color-cream)] text-[var(--color-ink-muted)] hover:bg-[var(--color-cream-dark)]"
                    }`}
                  >
                    Chain {chain.chainNumber}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedChain && (
            <div className="card-elevated p-6">
              <ChainVisualization chain={selectedChain} showAll={showAllLinks} onNarratorClick={setSelectedNarratorId} />
              {selectedChain.links.length > 3 && (
                <div className="text-center mt-4">
                  <button
                    onClick={() => setShowAllLinks(!showAllLinks)}
                    aria-expanded={showAllLinks}
                    className="text-sm text-[var(--color-gold)] hover:text-[var(--color-gold-dim)] font-medium flex items-center gap-1 mx-auto"
                  >
                    {showAllLinks ? (
                      <><ChevronUp className="w-4 h-4" /> Show less</>
                    ) : (
                      <><ChevronDown className="w-4 h-4" /> View all {selectedChain.links.length} narrators</>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <AnimatePresence>
        {selectedNarratorId && <NarratorModal narratorId={selectedNarratorId} onClose={() => setSelectedNarratorId(null)} />}
      </AnimatePresence>
    </div>
  );
}

/* ============================================
   Cards
============================================ */

function HadithCard({ hadith, onSelect }: { hadith: HadithSummary; onSelect: (id: string) => void }) {
  const { id, collection, hadithNumber, textEnglish, grading, chainCount } = hadith;
  return (
    <AnimatedCard>
      <button onClick={() => onSelect(id)} className="block w-full text-left card-elevated p-6 group">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-[var(--color-gold)]">{collection}</span>
            <span className="text-xs text-[var(--color-ink-muted)]">#{hadithNumber}</span>
          </div>
          {grading && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-cream)] text-[var(--color-gold-dim)] font-medium">{grading}</span>
          )}
        </div>
        <p className="text-sm text-[var(--color-midnight)] line-clamp-3 group-hover:text-[var(--color-gold-dim)] transition-colors">{textEnglish}</p>
        <span className="flex items-center gap-1 mt-4 text-xs text-[var(--color-gold)] font-medium">
          View {chainCount} {chainCount === 1 ? "chain" : "chains"} <ExternalLink className="w-3 h-3" aria-hidden />
        </span>
      </button>
    </AnimatedCard>
  );
}

function NarratorCard({ narrator, onSelect }: { narrator: NarratorSummary; onSelect: (id: string) => void }) {
  const { id, nameEnglish, nameArabic, generation, reliability } = narrator;
  const colors = genColor(generation);
  return (
    <AnimatedCard>
      <button onClick={() => onSelect(id)} className="block w-full text-left card-elevated p-6 group">
        <div className="flex items-start gap-4">
          <span className={`w-12 h-12 shrink-0 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center font-bold`} aria-hidden>
            {nameEnglish[0]}
          </span>
          <span className="flex-1">
            <span className="block font-semibold text-[var(--color-midnight)] group-hover:text-[var(--color-gold-dim)] transition-colors">{nameEnglish}</span>
            {nameArabic && <span lang="ar" className="block text-sm font-arabic text-[var(--color-ink-muted)]">{nameArabic}</span>}
            <span className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{colors.label}</span>
              {reliability && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--color-cream)] text-[var(--color-gold-dim)] capitalize">{label(reliability)}</span>
              )}
            </span>
          </span>
        </div>
      </button>
    </AnimatedCard>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="relative flex-1 min-w-[220px]">
      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-ink-muted)]" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full pl-11 pr-4 py-3 rounded-full border border-[var(--color-cream-dark)] bg-white text-sm focus:outline-none focus:border-[var(--color-gold)] focus:ring-2 focus:ring-[var(--color-gold)]/20 transition-all"
      />
    </div>
  );
}

/* ============================================
   Chains Tab
============================================ */

function ChainsTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [collection, setCollection] = useState("");
  const [page, setPage] = useState(1);
  const [selectedHadithId, setSelectedHadithId] = useState<string | null>(null);
  const search = useDebounce(searchQuery.trim());

  const { data: filters } = useIsnadFilters();
  const { data, isLoading, isError, error, refetch } = useHadiths({
    search: search || undefined,
    collection: collection || undefined,
    page,
    page_size: PAGE_SIZE,
  });
  const selected = useHadith(selectedHadithId ?? undefined);

  useEffect(() => {
    if (selected.data) {
      track({
        eventType: "view",
        domain: "isnad",
        entityType: "hadith",
        entityId: selected.data.id,
        data: { title: `${selected.data.collection} #${selected.data.hadithNumber}` },
      });
    }
  }, [selected.data]);

  if (selectedHadithId) {
    if (selected.isLoading) return <SkeletonCardGrid count={2} />;
    if (selected.isError || !selected.data) {
      return (
        <>
          <ErrorState error={selected.error} onRetry={selected.refetch} />
          <button onClick={() => setSelectedHadithId(null)} className="btn-outline mx-auto block">Back</button>
        </>
      );
    }
    return <HadithDetailView hadith={selected.data} onBack={() => setSelectedHadithId(null)} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <SearchBox value={searchQuery} onChange={(v) => { setSearchQuery(v); setPage(1); }} placeholder="Search hadith text" />
        {filters && filters.collections.length > 1 && (
          <select
            value={collection}
            onChange={(e) => { setCollection(e.target.value); setPage(1); }}
            aria-label="Collection"
            className="rounded-full border border-[var(--color-cream-dark)] bg-white px-4 text-sm"
          >
            <option value="">All collections</option>
            {filters.collections.map((c) => (
              <option key={c.collection} value={c.collection}>{c.collection} ({c.count})</option>
            ))}
          </select>
        )}
      </div>

      {isLoading ? (
        <SkeletonCardGrid />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : !data || data.hadiths.length === 0 ? (
        <EmptyState icon={<ScrollText className="w-12 h-12" />}>No hadiths match your search.</EmptyState>
      ) : (
        <>
          <div className="space-y-4">
            {data.hadiths.map((hadith) => (
              <HadithCard key={hadith.id} hadith={hadith} onSelect={setSelectedHadithId} />
            ))}
          </div>
          <Pagination page={page} total={data.total} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      )}
    </div>
  );
}

/* ============================================
   Narrators Tab
============================================ */

function NarratorsTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [generation, setGeneration] = useState<NarratorGeneration | "">("");
  const [page, setPage] = useState(1);
  const [selectedNarratorId, setSelectedNarratorId] = useState<string | null>(null);
  const search = useDebounce(searchQuery.trim());

  const { data, isLoading, isError, error, refetch } = useNarrators({
    search: search || undefined,
    generation: generation || undefined,
    page,
    page_size: PAGE_SIZE,
  });

  return (
    <div className="space-y-6">
      <SearchBox value={searchQuery} onChange={(v) => { setSearchQuery(v); setPage(1); }} placeholder="Search narrators" />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => { setGeneration(""); setPage(1); }} className={`pill ${generation === "" ? "active" : ""}`}>All generations</button>
        {(Object.keys(GEN_COLORS) as NarratorGeneration[]).map((gen) => (
          <button key={gen} onClick={() => { setGeneration(gen); setPage(1); }} className={`pill ${generation === gen ? "active" : ""}`}>
            {GEN_COLORS[gen].label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <SkeletonCardGrid />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : !data || data.narrators.length === 0 ? (
        <EmptyState icon={<Users className="w-12 h-12" />}>No narrators match your filters.</EmptyState>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.narrators.map((narrator) => (
              <NarratorCard key={narrator.id} narrator={narrator} onSelect={setSelectedNarratorId} />
            ))}
          </div>
          <Pagination page={page} total={data.total} pageSize={PAGE_SIZE} onChange={setPage} />
        </>
      )}

      <AnimatePresence>
        {selectedNarratorId && <NarratorModal narratorId={selectedNarratorId} onClose={() => setSelectedNarratorId(null)} />}
      </AnimatePresence>
    </div>
  );
}

/* ============================================
   Main Isnad Page
============================================ */

export default function IsnadPage() {
  const [activeTab, setActiveTab] = useState<"chains" | "narrators">("chains");
  useSEO("Isnad Explorer", "Explore the chains of narration (isnad) behind authentic hadith, with narrator biographies and reliability gradings.");
  usePageView("isnad");

  const tabClass = (tab: typeof activeTab) =>
    `px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
      activeTab === tab
        ? "bg-[var(--color-gold)] text-white"
        : "bg-[var(--color-cream)] text-[var(--color-ink-muted)] hover:bg-[var(--color-cream-dark)]"
    }`;

  return (
    <main className="pt-16">
      <div className="bg-gradient-to-b from-blue-950 to-[var(--color-midnight)] py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 pattern-islamic opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-blue-300 text-sm font-semibold uppercase tracking-[0.2em]">Science of Hadith</span>
            <h1 className="mt-3 font-[var(--font-display)] text-4xl md:text-5xl font-bold text-[var(--color-cream)]">Isnad Explorer</h1>
            <p className="mt-4 text-[var(--color-cream)]/60 max-w-2xl mx-auto">
              Explore chains of narration, read narrator biographies, and see how hadith were transmitted across generations.
            </p>
          </motion.div>
        </div>
      </div>

      <Section>
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="flex justify-center gap-4" role="tablist">
            <button role="tab" aria-selected={activeTab === "chains"} onClick={() => setActiveTab("chains")} className={tabClass("chains")}>
              Chains
            </button>
            <button role="tab" aria-selected={activeTab === "narrators"} onClick={() => setActiveTab("narrators")} className={tabClass("narrators")}>
              Narrators
            </button>
          </div>
          <div role="tabpanel">{activeTab === "chains" ? <ChainsTab /> : <NarratorsTab />}</div>
        </div>
      </Section>
    </main>
  );
}
